import { useQuery } from "@tanstack/react-query";
import {
  ApiClientError,
  INSTANCE_CHECK_HEADER,
  INSTANCE_REFUSAL_CODES,
  apiRequest,
} from "@/lib/api";
import { isInstanceIdShape } from "@/lib/instancePath";

export type InstanceRefusalCode = (typeof INSTANCE_REFUSAL_CODES)[number];

function isRefusalCode(code: string): code is InstanceRefusalCode {
  return (INSTANCE_REFUSAL_CODES as readonly string[]).includes(code);
}

/**
 * #384: why an address's instance cannot be opened, asked once of the server. `/me` lists no
 * instance that is disabled or whose tenant is, so the shell cannot tell those apart from one
 * the person never had; the server can, and tells only a person who holds a role there.
 *
 * The question is `GET /instances/current` for that id with the check header, so an unreachable
 * id answers 403 and the shell's lost-access handling (a 401 `INSTANCE_MISMATCH`) never runs for
 * it. Any answer that is not one of the closed codes (a mismatch, a network failure, an instance
 * that opens after all) is `null`: the ordinary "no access" page. A malformed id is not asked.
 */
export function useInstanceRefusal(instanceId: string | null): {
  pending: boolean;
  code: InstanceRefusalCode | null;
} {
  const ask = instanceId !== null && isInstanceIdShape(instanceId);
  const query = useQuery({
    queryKey: ["instance-refusal", instanceId],
    enabled: ask,
    retry: false,
    staleTime: 0,
    gcTime: 0,
    queryFn: async (): Promise<InstanceRefusalCode | null> => {
      try {
        await apiRequest("/api/v2/instances/current", {
          headers: { "x-instance-id": instanceId as string, [INSTANCE_CHECK_HEADER]: "1" },
        });
        return null;
      } catch (error) {
        if (error instanceof ApiClientError && isRefusalCode(error.code)) return error.code;
        return null;
      }
    },
  });
  return { pending: ask && query.isPending, code: query.data ?? null };
}
